import { useEffect, useRef, useState } from "react";
import { useInView, useMotionValue, animate } from "framer-motion";
import { Lines, Fade } from "../Reveal";
import { REGIONS, SRC, compute, fmt, fmtBig } from "./potentialData";

const Counter = ({ to, decimals = 0, testId }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const mv = useMotionValue(0);
  const [text, setText] = useState(fmt(0, decimals));

  useEffect(() => mv.on("change", (v) => setText(fmt(v, decimals))), [mv, decimals]);
  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(mv, to, { duration: 1.4, ease: [0.22, 1, 0.36, 1] });
    return () => controls.stop();
  }, [inView, mv, to]);

  return (
    <span ref={ref} data-testid={testId}>
      {text}
    </span>
  );
};

const rows = (r, c) => [
  {
    id: "cattle", value: c.cattle, decimals: 0, line: "cattle whose beef is thrown away",
    relate: `That is 1 in every ${c.oneIn} animals slaughtered for beef ${r.id === "eu" ? "in the EU" : "worldwide"}.`,
  },
  {
    id: "co2e", value: c.co2eMt, decimals: 1, line: "million tonnes of greenhouse gas",
    relate: `${c.co2eMt > r.ghgCountry.mt ? "More than" : "About what"} ${r.ghgCountry.name} emits in a whole year (${fmt(r.ghgCountry.mt, 0)} million tonnes).`,
  },
  {
    id: "cars", value: c.cars / 1e6, decimals: 1, line: "million cars' worth of emissions for a year",
    relate: `${c.cars > r.carCountry.cars ? "More than" : "Roughly"} every car on the road in ${r.carCountry.name} (${fmt(r.carCountry.cars / 1e6, 1)} million).`,
  },
  {
    id: "land", value: c.hectares / 1e6, decimals: 1, line: "million hectares of farmland",
    relate: `About the size of ${r.country.name} (${fmt((r.country.km2 * 100) / 1e6, 1)} million hectares).`,
  },
  {
    id: "water", value: c.litres / 1e9, decimals: 0, line: "billion litres of freshwater",
    relate: `Enough to fill ${fmtBig(Math.round(c.pools / 1000) * 1000)} Olympic swimming pools.`,
  },
  {
    id: "meals", value: c.portions / 1e6, decimals: 0, line: "million 200 g portions of beef",
    relate: `${fmt(c.portionsPerPerson, 1)} portions for every one of the ${r.populationLabel}.`,
  },
];

const SOURCES = [SRC.eurostatMeat, SRC.faoMeat, SRC.meatLoss, SRC.faoLoss, SRC.ec, SRC.yields, SRC.poore, SRC.epa, SRC.unfccc, SRC.acea, SRC.dft, SRC.worldBank, SRC.eurostatPop, SRC.unPop];

export const PotentialDiagram = () => {
  const [region, setRegion] = useState(REGIONS.eu);
  const calc = compute(region);
  const where = region.id === "eu" ? "in the EU" : "worldwide";

  return (
    <div className="px-6 md:px-10" data-testid="potential-diagram">
      <div className="grid grid-cols-12 gap-x-6 gap-y-10">
        <div className="col-span-12 md:col-span-6">
          <div className="flex items-center gap-8 mb-8" data-testid="potential-region">
            {Object.values(REGIONS).map((r) => {
              const active = r.id === region.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRegion(r)}
                  aria-pressed={active}
                  className={`mono text-xs tracking-[0.22em] uppercase py-3 min-w-[44px] border-b transition-colors ${
                    active ? "text-[#F2F3F5] border-[#F2F3F5]" : "text-[#F2F3F5]/45 border-transparent hover:text-[#F2F3F5]/80"
                  }`}
                  data-testid={`potential-region-${r.id}`}
                >
                  {r.name}
                </button>
              );
            })}
          </div>
          <Lines
            lines={["What a printed date", "throws away."]}
            className="display normal-case text-[1.6rem] sm:text-5xl lg:text-[3.8rem]"
            testId="potential-headline"
          />
        </div>
        <div className="col-span-12 md:col-span-5 md:col-start-8 md:pt-3">
          <Fade delay={0.2}>
            <p className="body-copy max-w-[40ch]" data-testid="potential-intro">
              Each year, about <span className="text-[#FCC83D]">{fmt(calc.basisT)} tonnes</span> of beef {where} is thrown away
              because a printed date said so, not because it had gone bad. Eating that beef instead would save, every year:
            </p>
            {region.dateAssumed && (
              <p className="mono text-[0.6rem] tracking-[0.15em] uppercase text-[#F2F3F5]/50 mt-4" data-testid="potential-assumption">
                Global figure uses the EU share of date-related waste; no worldwide share has been published.
              </p>
            )}
          </Fade>
        </div>
      </div>

      <ul className="mt-16 md:mt-24 grid grid-cols-12 gap-x-6 gap-y-12 md:gap-y-16" data-testid="potential-list">
        {rows(region, calc).map((row) => (
          <li key={row.id} className="col-span-12 sm:col-span-6 md:col-span-4 border-t border-[#F2F3F5]/20 pt-5" data-testid={`potential-node-${row.id}`}>
            <p className="display text-[2.6rem] sm:text-[3.2rem] lg:text-[3.8rem] leading-none">
              <Counter to={row.value} decimals={row.decimals} testId={`potential-value-${row.id}`} />
            </p>
            <p className="body-copy text-base md:text-lg text-[#F2F3F5]/80 mt-3 max-w-[26ch]">{row.line}</p>
            <p className="body-copy text-sm text-[#F2F3F5]/55 mt-3 max-w-[30ch]" data-testid={`potential-relate-${row.id}`}>{row.relate}</p>
          </li>
        ))}
      </ul>

      <p className="body-copy text-sm max-w-[70ch] mt-16 md:mt-24 text-[#F2F3F5]/60" data-testid="potential-limits">
        These are ceilings, not results: {fmt(region.productionT / 1e6, 1)} million tonnes of beef {where} × {fmt(region.lossShare * 100)} % lost or wasted × up to 10 %
        linked to date marking, then multiplied by published per-kilogram factors. Telltag has not prevented any of it yet.
      </p>
      <p className="mono text-[0.6rem] tracking-[0.15em] uppercase text-[#F2F3F5]/45 mt-5 max-w-[90ch] leading-relaxed" data-testid="potential-sources">
        Sources:{" "}
        {SOURCES.map((s, i) => (
          <span key={s.url}>
            <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-block py-1 underline underline-offset-4 decoration-[#F2F3F5]/25 hover:text-[#F2F3F5] transition-colors">
              {s.label.split(",")[0].split(" —")[0]}
            </a>
            {i < SOURCES.length - 1 ? " · " : ""}
          </span>
        ))}
      </p>
    </div>
  );
};
